import { useState } from "react";
import { Search } from "lucide-react";
import { fmtDuree, fmtNum } from "@/lib/format";
import { Panel } from "@/components/common/Panel";
import { cn } from "@/lib/utils";

/** Tableau détaillé dense : tri, recherche, pagination — données déjà agrégées/paginées backend. */
export const TableauDetail = ({ titre, colonnes, data, page, setPage, recherche, setRecherche,
                                actions, testId, onLigneClick }) => {
  const [tri, setTri] = useState(null);
  const items = [...(data?.items || [])];
  if (tri) {
    items.sort((a, b) => {
      const va = a[tri.cle], vb = b[tri.cle];
      if (va == null) return 1;
      if (vb == null) return -1;
      return (va > vb ? 1 : va < vb ? -1 : 0) * (tri.asc ? 1 : -1);
    });
  }
  const pages = data?.pages || 1;
  return (
    <Panel title={titre} testId={testId} actions={actions} bodyClassName="p-0">
      <div className="px-3 py-2 flex flex-wrap items-center gap-2 border-b border-border/50">
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input data-testid={`${testId}-recherche`} value={recherche} placeholder="Rechercher (machine, OF, cause, commentaire…)"
            onChange={(e) => { setRecherche(e.target.value); setPage(1); }}
            className="h-7 flex-1 bg-input border border-border rounded-sm px-2 text-[11px]" />
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">{fmtNum(data?.total || 0)} lignes</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs whitespace-nowrap">
          <thead className="bg-muted/40 sticky top-0">
            <tr className="text-left eyebrow">
              {colonnes.map((c) => (
                <th key={c.cle} className={cn("px-2 py-1.5 cursor-pointer select-none hover:text-foreground", c.align === "right" && "text-right")}
                  onClick={() => setTri((t) => ({ cle: c.cle, asc: t?.cle === c.cle ? !t.asc : true }))}>
                  {c.label}{tri?.cle === c.cle ? (tri.asc ? " ↑" : " ↓") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50 font-mono">
            {items.map((it, i) => (
              <tr key={it.id ?? i} data-testid={`${testId}-row-${i}`}
                onClick={() => onLigneClick?.(it)}
                className={cn("hover:bg-muted/20", onLigneClick && "cursor-pointer")}>
                {colonnes.map((c) => (
                  <td key={c.cle} className={cn("px-2 py-1.5", c.align === "right" && "text-right")}>
                    {c.render ? c.render(it) : (it[c.cle] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={colonnes.length} className="px-2 py-4 text-center text-muted-foreground">Aucun événement sur cette sélection</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="px-3 py-2 flex items-center justify-between border-t border-border/50 text-[11px] font-mono">
          <button data-testid={`${testId}-prev`} disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="h-7 px-2 border border-border rounded-sm disabled:opacity-40 hover:bg-muted">Précédent</button>
          <span>Page {page} / {pages}</span>
          <button data-testid={`${testId}-next`} disabled={page >= pages} onClick={() => setPage(page + 1)}
            className="h-7 px-2 border border-border rounded-sm disabled:opacity-40 hover:bg-muted">Suivant</button>
        </div>
      )}
    </Panel>
  );
};

export const fmtD = fmtDuree;
